import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import TextField from '@atoms/TextField';
import Button from '@atoms/Button';
import LoadingAnimation from '@atoms/LoadingAnimation';
import { useTheme } from '@mui/material/styles';
import { checkPassword, isPasswordValid } from '@utils/Helper/helper';
import TooltipForValidation from '@molecules/TooltipForValidation';
import { PASSWORD_PARAMETERS } from '@utils/constants';
import messages from '@utils/messages';

interface IExpiredPasswordModalProps {
  open: boolean;
  username: string;
  onClose: () => void;
  onPasswordReset: (currentPassword: string, newPassword: string, confirmPassword: string) => Promise<void>;
}

const ExpiredPasswordModal: React.FC<IExpiredPasswordModalProps> = ({
  open,
  username,
  onClose,
  onPasswordReset,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [passwordTooltipParams, setPasswordTooltipParams] = useState(
    PASSWORD_PARAMETERS,
  );

  const handleCurrentPasswordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    setFormData(prev => ({
      ...prev,
      currentPassword: event.target.value,
    }));
  }, []);

  const handleNewPasswordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    setFormData(prev => ({
      ...prev,
      newPassword: event.target.value,
    }));
  }, []);

  const handleConfirmPasswordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    setFormData(prev => ({
      ...prev,
      confirmPassword: event.target.value,
    }));
  }, []);

  const validatePassword = useCallback(() => {
    const { newPassword } = formData;
    const tooltipParams = checkPassword(newPassword, passwordTooltipParams);
    setPasswordTooltipParams(tooltipParams);
    return isPasswordValid(newPassword);
  }, [formData.newPassword, passwordTooltipParams]);

  const handleSubmit = useCallback(async () => {
    if (!formData.currentPassword) {
      setError(messages.EXPIRED_PASSWORD_MODAL.ERRORS.CURRENT_PASSWORD_REQUIRED);
      return;
    }

    if (!validatePassword()) {
      setError(messages.EXPIRED_PASSWORD_MODAL.ERRORS.PASSWORD_REQUIREMENTS_NOT_MET);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(messages.EXPIRED_PASSWORD_MODAL.ERRORS.PASSWORDS_DO_NOT_MATCH);
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError(messages.EXPIRED_PASSWORD_MODAL.ERRORS.SAME_AS_CURRENT);
      return;
    }

    try {
      setLoading(true);
      await onPasswordReset(
        formData.currentPassword,
        formData.newPassword,
        formData.confirmPassword
      );
      onClose();
    } catch (err: any) {
      setError(err.message || messages.EXPIRED_PASSWORD_MODAL.ERRORS.RESET_FAILED);
    } finally {
      setLoading(false);
    }
  }, [formData, validatePassword, onPasswordReset, onClose]);

  const handleClose = useCallback((event: any, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  }, [onClose]);

  const renderError = useCallback((errorMessage: string) => (
    <Typography
      variant="caption"
      sx={{
        color: theme.palette.error.main,
        display: 'block',
        mt: 1,
        textAlign: 'center',
      }}
    >
      {errorMessage}
    </Typography>
  ), [theme.palette.error.main]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h3" component="div">
          {messages.EXPIRED_PASSWORD_MODAL.TITLE}
        </Typography>
        <Typography variant="body2" color={theme.palette.text.secondary} sx={{ mt: 1 }}>
          {messages.EXPIRED_PASSWORD_MODAL.SUBTITLE}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <LoadingAnimation compact={true} />
        ) : (
          <Grid container spacing={3} sx={{ pt: 2 }}>
          <Grid item xs={12}>
            <TextField
              label={messages.EXPIRED_PASSWORD_MODAL.CURRENT_PASSWORD_LABEL}
              type="password"
              value={formData.currentPassword}
              handleChange={handleCurrentPasswordChange}
              textFieldSize="full"
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TooltipForValidation
              validationItems={passwordTooltipParams}
              heading={messages.EXPIRED_PASSWORD_MODAL.PASSWORD_REQUIREMENTS_HEADING}
            >
              <TextField
                label={messages.EXPIRED_PASSWORD_MODAL.NEW_PASSWORD_LABEL}
                type="password"
                value={formData.newPassword}
                handleChange={handleNewPasswordChange}
                textFieldSize="full"
                required
                onBlur={validatePassword}
              />
            </TooltipForValidation>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={messages.EXPIRED_PASSWORD_MODAL.CONFIRM_PASSWORD_LABEL}
              type="password"
              value={formData.confirmPassword}
              handleChange={handleConfirmPasswordChange}
              textFieldSize="full"
              required
            />
          </Grid>

          {error && (
            <Grid item xs={12}>
              {renderError(error)}
            </Grid>
          )}
        </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
        >
          {messages.EXPIRED_PASSWORD_MODAL.CANCEL_BUTTON}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ ml: 2 }}
        >
          {messages.EXPIRED_PASSWORD_MODAL.RESET_BUTTON}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpiredPasswordModal;